import { fabric } from 'fabric';
import type { EditorEventMap, EditorEventName, EditorPlugin, FabricPluginName, ObjectTypeDefinition } from 'modules/fabric-editor';
import { EventBus, type Listener, type Unsubscribe } from './event-bus';
import { ObjectRegistry } from './object-registry';
import { PluginRegistry } from './plugin-registry';

export interface Resolution {
  width: number;
  height: number;
}

export interface FabricEditorOptions {
  canvasEl: HTMLCanvasElement;
  resolution?: Resolution;
  fabricOptions?: fabric.ICanvasOptions;
  autoFit?: boolean;
}

export interface AddTextOptions {
  text: string;
  left?: number;
  top?: number;
  fontSize?: number;
  fill?: string;
}

export interface AddImageOptions {
  url: string;
  left?: number;
  top?: number;
  scale?: number;
}

export interface AddRectOptions {
  left?: number;
  top?: number;
  width: number;
  height: number;
  fill?: string;
}

export class FabricEditor {
  readonly canvas: fabric.Canvas;
  readonly bus = new EventBus<EditorEventMap>();
  readonly objectTypes = new ObjectRegistry();
  readonly objects: ObjectsFacade;

  resolution: Resolution;
  autoFit: boolean;

  private readonly plugins = new PluginRegistry();
  private readonly fabricUnbind: Array<() => void> = [];
  private disposed = false;

  constructor(options: FabricEditorOptions) {
    this.resolution = options.resolution ?? { width: 1920, height: 1080 };
    this.autoFit = options.autoFit ?? false;
    this.canvas = new fabric.Canvas(options.canvasEl, {
      preserveObjectStacking: true,
      ...options.fabricOptions,
    });
    this.objects = new ObjectsFacade(this);
    this.bindFabricEvents();
    if (this.autoFit) this.fit();
  }

  use(plugin: EditorPlugin): this {
    this.assertNotDisposed();
    this.plugins.install(this, plugin);
    return this;
  }

  hasPlugin(name: FabricPluginName): boolean {
    return this.plugins.has(name);
  }

  registerObjectType<TProps>(def: ObjectTypeDefinition<TProps>): this {
    this.objectTypes.register(def);
    return this;
  }

  on<K extends EditorEventName>(event: K, listener: Listener<EditorEventMap[K]>): Unsubscribe {
    return this.bus.on(event, listener);
  }

  off<K extends EditorEventName>(event: K, listener: Listener<EditorEventMap[K]>): void {
    this.bus.off(event, listener);
  }

  emit<K extends EditorEventName>(event: K, payload: EditorEventMap[K]): void {
    this.bus.emit(event, payload);
  }

  setResolution(resolution: Resolution): void {
    this.resolution = resolution;
    if (this.autoFit) this.fit();
  }

  setSize(width: number, height: number): void {
    this.canvas.setDimensions({ width, height });
    this.bus.emit('canvas:resized', { width, height });
    if (this.autoFit) this.fit();
  }

  fit(): void {
    const cw = this.canvas.getWidth();
    const ch = this.canvas.getHeight();
    const { width: rw, height: rh } = this.resolution;
    if (cw <= 0 || ch <= 0 || rw <= 0 || rh <= 0) return;
    const scale = Math.min(cw / rw, ch / rh);
    const tx = (cw - rw * scale) / 2;
    const ty = (ch - rh * scale) / 2;
    this.canvas.setViewportTransform([scale, 0, 0, scale, tx, ty]);
    this.canvas.requestRenderAll();
    this.bus.emit('zoom:changed', { zoom: scale });
  }

  isDisposed(): boolean {
    return this.disposed;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.plugins.disposeAll(this);
    for (const unbind of this.fabricUnbind) unbind();
    this.fabricUnbind.length = 0;
    this.bus.clear();
    try {
      this.canvas.dispose();
    } catch {
      // canvas may already be torn down by React strict-mode unmount
    }
  }

  private bindFabricEvents(): void {
    const onSelection = () => {
      this.bus.emit('selection:changed', { active: this.canvas.getActiveObjects() });
    };
    const onAdded = (e: fabric.IEvent) => {
      if (e.target) this.bus.emit('object:added', { target: e.target });
    };
    const onModified = (e: fabric.IEvent) => {
      if (e.target) this.bus.emit('object:modified', { target: e.target });
    };
    const onRemoved = (e: fabric.IEvent) => {
      if (e.target) this.bus.emit('object:removed', { target: e.target });
    };

    this.canvas.on('selection:created', onSelection);
    this.canvas.on('selection:updated', onSelection);
    this.canvas.on('selection:cleared', onSelection);
    this.canvas.on('object:added', onAdded);
    this.canvas.on('object:modified', onModified);
    this.canvas.on('object:removed', onRemoved);

    this.fabricUnbind.push(
      () => this.canvas.off('selection:created', onSelection as never),
      () => this.canvas.off('selection:updated', onSelection as never),
      () => this.canvas.off('selection:cleared', onSelection as never),
      () => this.canvas.off('object:added', onAdded as never),
      () => this.canvas.off('object:modified', onModified as never),
      () => this.canvas.off('object:removed', onRemoved as never),
    );
  }

  private assertNotDisposed(): void {
    if (this.disposed) throw new Error('FabricEditor has been disposed.');
  }
}

export class ObjectsFacade {
  constructor(private readonly editor: FabricEditor) {}

  addText(options: AddTextOptions): fabric.Textbox {
    const obj = new fabric.Textbox(options.text, {
      left: options.left ?? 100,
      top: options.top ?? 100,
      fontSize: options.fontSize ?? 32,
      fill: options.fill ?? '#000000',
    });
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  addRect(options: AddRectOptions): fabric.Rect {
    const obj = new fabric.Rect({
      left: options.left ?? 100,
      top: options.top ?? 100,
      width: options.width,
      height: options.height,
      fill: options.fill ?? '#cccccc',
    });
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  addImage(options: AddImageOptions): Promise<fabric.Image> {
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(
        options.url,
        (img) => {
          if (!img) {
            reject(new Error(`Failed to load image: ${options.url}`));
            return;
          }
          img.set({
            left: options.left ?? 100,
            top: options.top ?? 100,
          });
          if (options.scale !== undefined) img.scale(options.scale);
          this.editor.canvas.add(img);
          this.editor.canvas.setActiveObject(img);
          this.editor.canvas.requestRenderAll();
          resolve(img);
        },
        { crossOrigin: 'anonymous' },
      );
    });
  }

  addCustom<TProps>(kind: string, props: TProps): fabric.Object {
    const obj = this.editor.objectTypes.create(kind, props);
    this.editor.canvas.add(obj);
    this.editor.canvas.setActiveObject(obj);
    this.editor.canvas.requestRenderAll();
    return obj;
  }

  remove(obj: fabric.Object): void {
    this.editor.canvas.remove(obj);
    this.editor.canvas.requestRenderAll();
  }

  clear(): void {
    this.editor.canvas.clear();
    this.editor.canvas.requestRenderAll();
  }

  setProp<K extends keyof fabric.Object>(target: fabric.Object, key: K, value: fabric.Object[K]): void {
    target.set(key as keyof fabric.Object, value as never);
    target.setCoords();
    this.editor.canvas.requestRenderAll();
    this.editor.emit('object:modified', { target });
  }

  setData(target: fabric.Object, key: string, value: unknown): void {
    const prev = (target.data ?? {}) as Record<string, unknown>;
    const next = { ...prev };
    if (value === undefined) delete next[key];
    else next[key] = value;
    target.data = next;
    this.editor.canvas.requestRenderAll();
    this.editor.emit('object:modified', { target });
  }
}
